<!--
  LongFormCard — the all-fields-visible form for the "Autre" sub-profiles
  (MAM / RPE / trainer / agency), ported from the prototype's shared/LongFormCard.vue.
  4 field configs keyed by `type`. Required fields gate the CTA; format checks
  (email, 5-digit postal code) run on submit; CGU is mandatory + unchecked by
  default (RGPD). Emits `submit` with all fields + cguAccepted.
-->
<template>
  <div class="long-form-card">
    <div class="long-form-fields">
      <div v-for="f in config.fields" :key="f.key" class="form-field">
        <label class="form-lbl">{{ f.label }}</label>

        <!-- Gender toggle -->
        <div v-if="f.type === 'gender'" class="gender-toggle">
          <button
            type="button"
            class="gender-btn"
            :class="{ active: form[f.key] === 'femme' }"
            @click="form[f.key] = 'femme'"
          >
            👩 Femme
          </button>
          <button
            type="button"
            class="gender-btn"
            :class="{ active: form[f.key] === 'homme' }"
            @click="form[f.key] = 'homme'"
          >
            👨 Homme
          </button>
        </div>

        <!-- Textarea (MAM presentation) -->
        <textarea
          v-else-if="f.type === 'textarea'"
          v-model="form[f.key]"
          class="form-input form-textarea"
          :placeholder="f.placeholder || ''"
          rows="4"
        />

        <!-- Standard input (text, email) -->
        <input
          v-else
          v-model="form[f.key]"
          :type="f.type"
          class="form-input"
          :placeholder="f.placeholder || ''"
          :maxlength="f.maxLength"
          :autocomplete="f.autocomplete || 'off'"
        />

        <div v-if="errors[f.key]" class="form-err">{{ errors[f.key] }}</div>
      </div>
    </div>

    <!-- CGU (mandatory) -->
    <label class="form-check">
      <input v-model="cguAccepted" type="checkbox" />
      <span>
        J'accepte les
        <a href="/terms-of-use" target="_blank" rel="noopener"
          >Conditions générales d'utilisation et d'abonnement</a
        >
      </span>
    </label>
    <div v-if="errors.cgu" class="form-err">{{ errors.cgu }}</div>

    <button type="button" class="cta cta-blue long-form-cta" :disabled="!canSubmit || disabled" @click="onSubmit">
      {{ config.ctaLabel }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue'

type FieldType = 'gender' | 'text' | 'email' | 'textarea'

interface FormField {
  key: string
  type: FieldType
  label: string
  required?: boolean
  placeholder?: string
  maxLength?: number
  autocomplete?: string
}

interface FormConfig {
  title: string
  fields: FormField[]
  ctaLabel: string
}

export interface LongFormPayload {
  cguAccepted: boolean
  [field: string]: string | boolean | null
}

const props = withDefaults(
  defineProps<{
    type: 'mam' | 'rpe' | 'trainer' | 'agency'
    disabled?: boolean
  }>(),
  { disabled: false },
)

const emit = defineEmits<{ submit: [payload: LongFormPayload] }>()

const FORM_CONFIGS: Record<string, FormConfig> = {
  mam: {
    title: "Maison d'assistantes maternelles",
    fields: [
      { key: 'gender', type: 'gender', label: 'Vous êtes', required: true },
      { key: 'prenom', type: 'text', label: 'Prénom', required: false, autocomplete: 'given-name' },
      {
        key: 'nom',
        type: 'text',
        label: 'Nom de famille',
        required: false,
        autocomplete: 'family-name',
      },
      { key: 'nomStructure', type: 'text', label: 'Nom de la MAM', required: true },
      {
        key: 'email',
        type: 'email',
        label: 'Adresse email',
        required: true,
        autocomplete: 'email',
      },
      {
        key: 'presentation',
        type: 'textarea',
        label: 'Présentation de la MAM',
        required: false,
        placeholder: 'Présentez votre MAM en quelques lignes (lieu, projet pédagogique, équipe…)',
      },
    ],
    ctaLabel: 'Valider et créer mon compte',
  },
  rpe: {
    title: 'Relais petite enfance',
    fields: [
      { key: 'gender', type: 'gender', label: 'Vous êtes', required: true },
      { key: 'prenom', type: 'text', label: 'Prénom', required: false, autocomplete: 'given-name' },
      {
        key: 'nom',
        type: 'text',
        label: 'Nom de famille',
        required: false,
        autocomplete: 'family-name',
      },
      { key: 'nomStructure', type: 'text', label: 'Nom du RPE', required: true },
      {
        key: 'ville',
        type: 'text',
        label: 'Ville',
        required: true,
        autocomplete: 'address-level2',
      },
      {
        key: 'codePostal',
        type: 'text',
        label: 'Code postal',
        required: true,
        maxLength: 5,
        autocomplete: 'postal-code',
      },
      {
        key: 'email',
        type: 'email',
        label: 'Adresse email',
        required: true,
        autocomplete: 'email',
      },
    ],
    ctaLabel: 'Créer mon compte gratuit',
  },
  trainer: {
    title: 'Formateur',
    fields: [
      { key: 'gender', type: 'gender', label: 'Vous êtes', required: true },
      { key: 'prenom', type: 'text', label: 'Prénom', required: false, autocomplete: 'given-name' },
      {
        key: 'nom',
        type: 'text',
        label: 'Nom de famille',
        required: false,
        autocomplete: 'family-name',
      },
      { key: 'nomStructure', type: 'text', label: "Nom de l'organisme", required: true },
      {
        key: 'ville',
        type: 'text',
        label: 'Ville',
        required: true,
        autocomplete: 'address-level2',
      },
      {
        key: 'codePostal',
        type: 'text',
        label: 'Code postal',
        required: true,
        maxLength: 5,
        autocomplete: 'postal-code',
      },
      {
        key: 'email',
        type: 'email',
        label: 'Adresse email',
        required: true,
        autocomplete: 'email',
      },
    ],
    ctaLabel: 'Créer mon compte gratuit',
  },
  agency: {
    title: 'Agence',
    fields: [
      {
        key: 'raisonSociale',
        type: 'text',
        label: 'Raison sociale',
        required: true,
        placeholder: 'Ex: SAS Mon Entreprise',
      },
      { key: 'prenom', type: 'text', label: 'Prénom', required: false, autocomplete: 'given-name' },
      {
        key: 'nom',
        type: 'text',
        label: 'Nom de famille',
        required: false,
        autocomplete: 'family-name',
      },
      {
        key: 'email',
        type: 'email',
        label: 'Adresse email',
        required: true,
        autocomplete: 'email',
      },
    ],
    ctaLabel: 'Créer mon compte gratuit',
  },
}

const config = computed<FormConfig>(() => FORM_CONFIGS[props.type] || FORM_CONFIGS.mam!)

// Form state — initialised from the (fixed-per-mount) config fields.
const form = reactive<Record<string, string | null>>({})
for (const f of config.value.fields) {
  form[f.key] = f.type === 'gender' ? null : ''
}
const cguAccepted = ref(false)
const errors = ref<Record<string, string>>({})

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// CTA enabled only when every required field is filled AND CGU is checked
// (format validation happens on submit).
const canSubmit = computed(() => {
  for (const f of config.value.fields) {
    if (!f.required) continue
    const val = form[f.key]
    if (val === null || val === undefined) return false
    if (typeof val === 'string' && !val.trim()) return false
  }
  return cguAccepted.value
})

function validate(): boolean {
  const errs: Record<string, string> = {}
  for (const f of config.value.fields) {
    const val = form[f.key]
    const isEmpty = val === null || val === undefined || (typeof val === 'string' && !val.trim())
    if (f.required && isEmpty) {
      errs[f.key] = 'Ce champ est obligatoire'
      continue
    }
    if (f.type === 'email' && val && !EMAIL_REGEX.test(val.trim())) {
      errs[f.key] = 'Format email invalide'
    }
    if (f.key === 'codePostal' && val && !/^\d{5}$/.test(val.trim())) {
      errs[f.key] = 'Code postal invalide (5 chiffres)'
    }
  }
  if (!cguAccepted.value) errs.cgu = 'Veuillez accepter les CGU pour continuer'
  errors.value = errs
  return Object.keys(errs).length === 0
}

function onSubmit(): void {
  if (!validate()) return
  const payload: LongFormPayload = {
    cguAccepted: cguAccepted.value,
  }
  for (const f of config.value.fields) {
    const val = form[f.key]
    payload[f.key] = typeof val === 'string' ? val.trim() : (val ?? null)
  }
  emit('submit', payload)
}
</script>

<style scoped>
.long-form-card {
  width: 92%;
  margin: 0 auto;
  background: var(--white);
  border: 1.5px solid #e2edf5; /* off-palette — DS pass to arbitrate */
  border-radius: 16px;
  padding: 18px;
  box-shadow: 0 4px 20px rgba(27, 65, 89, 0.08);
  animation: fadeUp 0.3s both;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.long-form-fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.form-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.form-lbl {
  font-size: 11px;
  font-weight: 700;
  color: var(--grey-500);
  text-transform: uppercase;
  letter-spacing: 1.2px;
}
.form-input {
  width: 100%;
  padding: 11px 12px;
  background: #f8fbfd; /* off-palette — DS pass to arbitrate */
  border: 1.5px solid #e2edf5;
  border-radius: 10px;
  font-size: 14px;
  font-family: inherit;
  color: var(--navy-800);
  outline: none;
  transition:
    border-color 0.15s,
    background 0.15s;
}
.form-input:focus {
  border-color: var(--brand-default);
  background: var(--white);
}
.form-input::placeholder {
  color: #9bbdd4;
}
.form-textarea {
  min-height: 88px;
  resize: vertical;
  line-height: 1.5;
}
.gender-toggle {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  background: #f5fafd; /* off-palette — DS pass to arbitrate */
  border-radius: 10px;
  padding: 4px;
}
.gender-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 9px 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--grey-500);
  font-family: inherit;
  transition: all 0.15s;
}
.gender-btn:hover {
  color: var(--navy-800);
}
.gender-btn.active {
  background: var(--white);
  color: var(--navy-800);
  font-weight: 700;
  box-shadow: 0 1px 3px rgba(27, 65, 89, 0.08);
}
.form-err {
  font-size: 11.5px;
  color: #dc2626; /* off-palette error red — DS pass to arbitrate */
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 4px;
}
.form-err::before {
  content: '⚠';
  font-size: 12px;
}
.form-check {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  font-size: 12.5px;
  color: var(--grey-500);
  line-height: 1.45;
  cursor: pointer;
}
.form-check input {
  margin-top: 2px;
  flex-shrink: 0;
  cursor: pointer;
}
.form-check :deep(a) {
  color: var(--blue-800);
  text-decoration: underline;
}
.long-form-cta {
  margin-top: 6px;
  width: 100% !important;
}
.cta {
  padding: 13px 16px;
  border: none;
  border-radius: 12px;
  background: var(--brand-default);
  color: var(--white);
  font-size: 14px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  line-height: 14px;
  transition:
    background 0.15s,
    opacity 0.15s;
}
.cta:hover:not(:disabled) {
  background: var(--blue-800);
}
.cta:disabled {
  background: #e2edf5; /* off-palette disabled grey — DS pass to arbitrate */
  color: #9bbdd4;
  cursor: not-allowed;
}
.cta-blue {
  background: var(--brand-default);
}
@media (prefers-reduced-motion: reduce) {
  .long-form-card {
    animation: none;
  }
}
</style>
